import { Component, DoCheck, EventEmitter, Input, Output } from '@angular/core'
import { Router } from '@angular/router'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { SyncerService } from 'src/app/integrations/syncer.service'

@Component({
  selector: 'app-import-source',
  templateUrl: './import-source.component.html',
  styleUrls: ['./import-source.component.scss']
})
export class ImportSourceComponent implements DoCheck {
  @Input() importType: 'products' | 'categories' | 'orders' = 'products'
  @Input() importingType: string = ''
  @Input() corruptedImages: string[] = []
  @Input() availableSourceIds: string[] = ['csv', 'woocommerce', 'odoo', 'zoho']
  @Output() back = new EventEmitter<void>()
  @Output() importCsv = new EventEmitter<{ file: File; skipImages: boolean }>()
  @Output() importDirect = new EventEmitter<string>()
  @Output() viewResults = new EventEmitter<void>()

  importStep: 'select' | 'form' | 'importing' | 'success' = 'select'
  selectedSource: string | null = null
  csvFile: File | null = null
  csvFileName: string = ''
  skipImages: boolean = false
  showWarningModal: boolean = false

  allSources = [
    {
      id: 'csv',
      name: 'CSV File',
      desc: 'Upload a CSV file with your data.',
      icon: null,
      types: ['products', 'categories']
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      desc: 'Sync directly from your WordPress store.',
      icon: '/assets/images/integrations/woocommerce.svg',
      types: ['products', 'categories', 'orders']
    },
    {
      id: 'odoo',
      name: 'Odoo',
      desc: 'Import data from Odoo ERP.',
      icon: '/assets/images/integrations/odoo.svg',
      types: ['products', 'categories']
    },
    {
      id: 'zoho',
      name: 'Zoho Inventory',
      desc: 'Connect your Zoho Inventory account.',
      icon: '/assets/images/integrations/zoho.svg',
      types: ['products']
    }
  ]

  constructor(
    public Syncer: SyncerService,
    public builderService: BuilderService,
    private router: Router
  ) {}

  get sources() {
    return this.allSources.filter(
      s => s.types.includes(this.importType) && this.availableSourceIds.includes(s.id)
    )
  }

  get importTypeLabel(): string {
    const map: any = { products: 'Products', categories: 'Categories', orders: 'Orders' }
    return map[this.importType] || this.importType
  }

  get selectedSourceData() {
    return this.allSources.find(s => s.id === this.selectedSource)
  }

  get sectionTitle(): string {
    if (!this.selectedSource) return ''
    if (this.selectedSource === 'csv') {
      const verb = this.importStep === 'importing' ? 'ing' : ''
      return `Import${verb} ${this.importTypeLabel} as CSV`
    }
    return `Import ${this.importTypeLabel} from ${this.selectedSourceData?.name}`
  }

  get progressMessage(): string {
    if (this.selectedSource === 'csv') return 'Uploading CSV file...'
    return `Importing from ${this.selectedSourceData?.name}...`
  }

  get canStartImport(): boolean {
    if (!this.selectedSource) return false
    if (this.selectedSource === 'csv') return this.csvFile !== null
    return true
  }

  get successViewLabel(): string {
    const map: any = { products: 'Products', categories: 'Categories', orders: 'Orders' }
    return map[this.importType] || this.importType
  }

  ngDoCheck() {
    if (this.importStep === 'importing') {
      const csvDone =
        this.selectedSource === 'csv' &&
        this.Syncer.syncProgress !== undefined &&
        this.Syncer.syncProgress >= 100
      const directDone =
        this.selectedSource !== 'csv' && !this.builderService.showUpdateProgress
      if (csvDone || directDone) {
        this.importStep = 'success'
      }
    }
  }

  selectSource(sourceId: string) {
    this.selectedSource = sourceId
    this.importStep = 'form'
  }

  onBack() {
    if (this.importStep === 'form') {
      this.importStep = 'select'
      this.selectedSource = null
      this.csvFile = null
      this.csvFileName = ''
    } else {
      this.back.emit()
    }
  }

  onCancel() {
    this.importStep = 'select'
    this.selectedSource = null
    this.csvFile = null
    this.csvFileName = ''
    this.skipImages = false
  }

  onFileSelected(event: any) {
    const file = event.target.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file.')
      return
    }
    this.csvFile = file
    this.csvFileName = file.name
  }

  removeCsvFile(event: Event) {
    event.stopPropagation()
    this.csvFile = null
    this.csvFileName = ''
  }

  onStartImport() {
    if (this.selectedSource === 'csv' && this.csvFile) {
      this.importStep = 'importing'
      this.importCsv.emit({ file: this.csvFile, skipImages: this.skipImages })
    } else if (this.selectedSource) {
      this.showWarningModal = true
    }
  }

  onConfirmImport() {
    this.showWarningModal = false
    this.importDirect.emit(this.selectedSource!)
    this.importStep = 'importing'
  }

  onCancelWarning() {
    this.showWarningModal = false
  }

  onViewResults() {
    this.viewResults.emit()
    const routes: any = {
      products: ['/products'],
      categories: ['/categories/categories'],
      orders: ['/orders']
    }
    this.router.navigate(routes[this.importType] || ['/products'])
    // Reset for next use
    this.importStep = 'select'
    this.selectedSource = null
    this.csvFile = null
    this.Syncer.syncProgress = undefined
  }

  reset() {
    this.importStep = 'select'
    this.selectedSource = null
    this.csvFile = null
    this.csvFileName = ''
    this.skipImages = false
    this.Syncer.syncProgress = undefined
  }
}
